import { isEmpty } from '@sgme/fp';
import type { IntlKey } from 'locales';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import { useCallback } from 'react';
import { isStackTrace } from 'utils/errors/errors';
import { camelCaseToWords } from 'utils/string';

import { useSPIntl } from './useIntl';

export function useWorkflowErrorsUtils() {
  const { formatMessage } = useSPIntl();

  const getWorkflowErrorMessage = useCallback(
    (workflowError: NextWorkflowError, useRejectionCode: boolean = false, productSubtype?: string) => {
      return useRejectionCode
        ? formatMessage({
            id: `quote.errorMessages.${productSubtype}.rejectionCode.${workflowError.rejectionCode}` as IntlKey,
            defaultMessage: formatMessage({
              id: `quote.errorMessages.${productSubtype}.rejectionCode` as IntlKey,
              defaultMessage: getDefaultWorkflowErrorMessage(workflowError) ?? defaultErrorMessage,
            }),
          })
        : (getDefaultWorkflowErrorMessage(workflowError) ?? defaultErrorMessage);
    },
    [formatMessage],
  );

  const getWorkflowErrorsSummary = useCallback(
    (workflowErrors: readonly NextWorkflowError[], useRejectionCode: boolean = false, productSubtype?: string) => {
      const workFlowErrorMessages = workflowErrors.map(workflowError =>
        getWorkflowErrorMessage(workflowError, useRejectionCode, productSubtype),
      );

      const distinctWorkFlowErrorMessages = [...new Set(workFlowErrorMessages)];

      return distinctWorkFlowErrorMessages;
    },
    [getWorkflowErrorMessage],
  );

  const getFormattedWorkflowErrorsSummary = useCallback(
    (workflowErrors: readonly NextWorkflowError[], useRejectionCode: boolean = false, productSubtype?: string) => {
      const summaryFromErrorMessages = getWorkflowErrorsSummary(workflowErrors, useRejectionCode, productSubtype);
      return summaryFromErrorMessages.map(msg => msg.split('\r\n'));
    },
    [getWorkflowErrorsSummary],
  );

  const errorsToString = useCallback(
    (workflowErrors: readonly NextWorkflowError[]) => {
      const result = workflowErrors
        .map(error =>
          Object.entries(error)
            .filter(([key, value]) => !isEmpty(key) && !isEmpty(value))
            .reduce(
              (acc, [key, value]) =>
                `${acc}${formatMessage({ id: key as IntlKey, defaultMessage: camelCaseToWords(key) })}: ${formatMessage({ id: value as IntlKey, defaultMessage: value })}\n`,
              '',
            ),
        )
        .join('\n');
      return isEmpty(result) ? undefined : result;
    },
    [formatMessage],
  );

  return {
    getWorkflowErrorMessage,
    getWorkflowErrorsSummary,
    getFormattedWorkflowErrorsSummary,
    errorsToString,
  };
}

const defaultErrorMessage = 'An error occurred, please try again or contact your Sales representative.';

export function getDefaultWorkflowErrorMessage({
  originMessage,
  rejectionCode,
  rejectionMessage,
}: NextWorkflowError): string | null {
  if (rejectionMessage && !isStackTrace(rejectionMessage)) {
    return rejectionMessage;
  }

  if (originMessage && !isStackTrace(originMessage)) {
    return originMessage;
  }

  if (rejectionCode && !isStackTrace(rejectionCode)) {
    return rejectionCode;
  }

  return null;
}
